import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, Mail, MessageCircle, Phone, Send, Share2, X } from "lucide-react";

function BrandMark({ provider }) {
  if (provider === "whatsapp") {
    return (
      <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white shadow-sm" aria-hidden="true">
        <MessageCircle size={25} strokeWidth={2.2} />
        <Phone size={11} strokeWidth={2.8} className="absolute" />
      </span>
    );
  }
  if (provider === "facebook") {
    return (
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1877F2] font-sans text-[28px] font-bold leading-none text-white shadow-sm" aria-hidden="true">
        f
      </span>
    );
  }
  if (provider === "linkedin") {
    return (
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0A66C2] font-sans text-[18px] font-bold leading-none text-white shadow-sm" aria-hidden="true">
        in
      </span>
    );
  }
  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#229ED9] text-white shadow-sm" aria-hidden="true">
      <Send size={21} fill="currentColor" />
    </span>
  );
}

function SocialButton({ provider, label, detail, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[78px] items-center gap-3 rounded-2xl border-2 border-ink bg-white px-3 py-3 text-left shadow-[2px_2px_0_#171717] transition hover:-translate-y-0.5 hover:bg-[#fffaf1] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
      data-testid={`share-provider-${provider}`}
    >
      <BrandMark provider={provider} />
      <span className="min-w-0">
        <strong className="block text-sm text-ink">{label}</strong>
        <small className="block truncate text-[10px] text-muted">{detail}</small>
      </span>
    </button>
  );
}

function fallbackCopy(value) {
  const area = document.createElement("textarea");
  area.value = value;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  const copied = document.execCommand?.("copy");
  area.remove();
  if (!copied) throw new Error("Copia non disponibile");
}

export function ShareDialog({ post, topicSlug, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = useMemo(() => {
    if (!post || typeof window === "undefined") return "";
    const url = new URL(window.location.origin);
    url.searchParams.set("post", post.id);
    const slug = post.topicSlug || topicSlug;
    if (slug) url.searchParams.set("topic", slug);
    return url.toString();
  }, [post, topicSlug]);
  const shareText = useMemo(
    () => post ? `${post.author}: ${post.text || "Messaggio vocale su You Voice"}` : "",
    [post],
  );

  useEffect(() => {
    if (!post) return undefined;
    setCopied(false);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [post, onClose]);

  if (!post) return null;

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const targets = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
  };

  const openExternal = (provider) => {
    window.open(targets[provider], "_blank", "noopener,noreferrer");
    onClose?.();
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(shareUrl);
      else fallbackCopy(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: "You Voice", text: shareText, url: shareUrl });
      onClose?.();
    } catch (error) {
      if (error?.name !== "AbortError") setCopied(false);
    }
  };

  const mailHref = `mailto:?subject=${encodeURIComponent("Messaggio da You Voice")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-end justify-center sm:items-center sm:p-4"
      data-testid={`share-menu-${post.id}`}
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-ink/65 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Chiudi condivisione"
        data-testid="share-backdrop"
      />
      <section
        className="relative z-[1401] max-h-[92dvh] w-full overflow-y-auto rounded-t-[30px] border-2 border-b-0 border-ink bg-cream p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-5px_0_#171717] sm:max-w-[560px] sm:rounded-[30px] sm:border-b-2 sm:p-5 sm:shadow-[8px_8px_0_#171717]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`share-title-${post.id}`}
      >
        <header className="flex items-start gap-3 border-b-2 border-ink pb-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-primary text-white shadow-[2px_2px_0_#171717]">
            <Share2 size={21} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">You Voice</p>
            <h2 id={`share-title-${post.id}`} className="font-display text-xl font-bold leading-tight text-ink">Condividi messaggio</h2>
            <p className="mt-1 truncate text-xs text-muted">di {post.author || "Utente"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-white text-primary shadow-[2px_2px_0_#171717] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
            aria-label="Chiudi"
            data-testid="share-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="my-4 rounded-2xl border border-edge bg-white/70 px-4 py-3">
          <p className="line-clamp-2 text-sm leading-5 text-ink">{post.text || "Messaggio vocale su You Voice"}</p>
        </div>

        <div className="grid grid-cols-1 gap-2 min-[390px]:grid-cols-2">
          <SocialButton provider="whatsapp" label="WhatsApp" detail="Invia a chat e gruppi" onClick={() => openExternal("whatsapp")} />
          <SocialButton provider="facebook" label="Facebook" detail="Pubblica o condividi" onClick={() => openExternal("facebook")} />
          <SocialButton provider="linkedin" label="LinkedIn" detail="Condividi nella tua rete" onClick={() => openExternal("linkedin")} />
          <SocialButton provider="telegram" label="Telegram" detail="Invia a contatti e canali" onClick={() => openExternal("telegram")} />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <a
            href={mailHref}
            onClick={onClose}
            className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-ink bg-white px-3 text-sm font-bold text-ink shadow-[2px_2px_0_#171717] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
            data-testid="share-email"
          >
            <Mail size={17} className="text-primary" /> Email
          </a>
          <button
            type="button"
            onClick={copyLink}
            className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-ink bg-white px-3 text-sm font-bold text-ink shadow-[2px_2px_0_#171717] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
            data-testid="share-copy"
          >
            {copied ? <Check size={17} className="text-emerald-700" /> : <Copy size={17} className="text-primary" />}
            {copied ? "Copiato" : "Copia link"}
          </button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button
              type="button"
              onClick={nativeShare}
              className="col-span-2 flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-ink bg-ink px-3 text-sm font-bold text-white shadow-[2px_2px_0_#d9302a] sm:col-span-1"
              data-testid="share-native"
            >
              <Share2 size={17} /> Altre app
            </button>
          )}
        </div>

        <p className="mt-4 rounded-2xl border border-primary/25 bg-white px-3 py-2 text-[11px] leading-4 text-muted">
          Il pulsante <strong className="text-primary">Diffondi</strong> resta separato: pubblica il messaggio nella rete interna di You Voice.
        </p>
      </section>
    </div>,
    document.body,
  );
}
